//Inline mention token format.
//
//Mentions are embedded directly in the message/comment/post text as tokens:
//    ⟦type:id:label⟧
//e.g. ⟦person:2f1c...:Jane Doe⟧ or ⟦event:9a3e...:Sunday Service⟧
//
//This makes the text column self-contained (no separate mentions array / offsets)
//so it always renders correctly later, survives edits, and needs no schema column.
//
//The brackets ⟦ (U+27E6) and ⟧ (U+27E7) are extremely unlikely to appear in user
//text. `type` is lowercase letters, `id` is a uuid/url with no colon, and `label`
//is everything up to the closing bracket (so labels may contain colons safely).

const MENTION_OPEN = '\u27E6';  // ⟦
const MENTION_CLOSE = '\u27E7'; // ⟧

//Global regex for scanning/splitting. type=lowercase letters, id=no colon or close,
//label=anything up to the close bracket.
const MENTION_REGEX = /\u27E6([a-z]+):([^:\u27E7]+):([^\u27E7]*)\u27E7/g;

//Build a token from an entity. Strips bracket chars from the label to stay parseable.
const encodeMention = ({ type, id, label }) => {
    const safeLabel = String(label ?? '').replace(/[\u27E6\u27E7]/g, '');
    return `${MENTION_OPEN}${type}:${id}:${safeLabel}${MENTION_CLOSE}`;
};

//Split text into ordered segments for rendering.
//Returns array of { text } and { mention: {type, id, label} } segments.
const parseMentionSegments = (text) => {
    if (!text || typeof text !== 'string') return text ? [{ text }] : [];

    const segments = [];
    let lastIndex = 0;
    let match;

    //Reset stateful global regex before iterating
    MENTION_REGEX.lastIndex = 0;
    while ((match = MENTION_REGEX.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ text: text.slice(lastIndex, match.index) });
        }
        segments.push({ mention: { type: match[1], id: match[2], label: match[3] } });
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        segments.push({ text: text.slice(lastIndex) });
    }

    return segments;
};

//Replace tokens with a plain "@label" (for previews, notifications, single-line text).
const stripMentions = (text) => {
    if (!text || typeof text !== 'string') return text ?? '';
    return text.replace(MENTION_REGEX, (_m, _type, _id, label) => `@${label}`);
};

//Extract the mentioned entities from text: [{type, id, label}, ...]
const extractMentions = (text) => {
    if (!text || typeof text !== 'string') return [];
    const out = [];
    MENTION_REGEX.lastIndex = 0;
    let match;
    while ((match = MENTION_REGEX.exec(text)) !== null) {
        out.push({ type: match[1], id: match[2], label: match[3] });
    }
    return out;
};

//True if the text contains any mention tokens
const hasMentions = (text) => {
    if (!text || typeof text !== 'string') return false;
    MENTION_REGEX.lastIndex = 0;
    return MENTION_REGEX.test(text);
};

//Convert token-embedded text back into a display string (labels in place of tokens)
//plus the mentions list, so the MessageInput can be prefilled for editing.
//Returns { displayText, mentions } where mentions is [{type, id, label}, ...].
const decodeMentionsForEditing = (text) => {
    if (!text || typeof text !== 'string') return { displayText: '', mentions: [] };
    const mentions = extractMentions(text);
    const displayText = text.replace(MENTION_REGEX, (_m, _type, _id, label) => label);
    return { displayText, mentions };
};

export {
    MENTION_OPEN,
    MENTION_CLOSE,
    MENTION_REGEX,
    encodeMention,
    parseMentionSegments,
    stripMentions,
    extractMentions,
    hasMentions,
    decodeMentionsForEditing,
};
